import { ReactElement } from 'react'

import { useAtomValue } from 'jotai'

import {
  Button,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import {
  humanReadableInvoiceNumberAtom,
  invoiceCustomerLegalCompanyNameAtom,
  invoiceIdAtom,
  invoicePaymentStatusAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

export const ButtonSendPaymentReminder = () => {
  const invoicePaymentStatus = useAtomValue(invoicePaymentStatusAtom)
  const { can } = usePermissions()

  if (!invoicePaymentStatus) return null

  const showForPaymentStatuses: Dashboardv99990101Api.Invoices.InvoicePaymentStatus[] =
    ['UNPAID', 'PARTIALLY_PAID']

  if (!showForPaymentStatuses.includes(invoicePaymentStatus)) return null

  const trigger = (
    <Button variant="primary" data-testid="header.button.send-payment-reminder">
      Send payment reminder
    </Button>
  )

  if (!can('org.invoice:send')) {
    return (
      <ProtectedButton permission="org.invoice:send">{trigger}</ProtectedButton>
    )
  }

  return <DialogSendPaymentReminder trigger={trigger} />
}

const DialogSendPaymentReminder = ({ trigger }: { trigger: ReactElement }) => {
  const { displayNotification } = useNotifications()

  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceNumber = useAtomValue(humanReadableInvoiceNumberAtom)
  const customerLegalCompanyName = useAtomValue(
    invoiceCustomerLegalCompanyNameAtom
  )

  const sendPaymentReminderMutation = useMutation(
    dashboard20240730Client.postSendPaymentReminderForInvoice,
    {
      onSuccess: () => {
        displayNotification('Payment reminder sent', { type: 'success' })
      },
      onError: () => {
        displayNotification('Failed to send payment reminder', {
          type: 'error'
        })
      }
    }
  )

  return (
    <SimpleModal
      trigger={trigger}
      variant="dialog"
      title="Send payment reminder"
      submitButtonText="Send reminder"
      submittingButtonText="Sending..."
      onSubmit={async () => {
        if (!invoiceId) return false
        const res = await sendPaymentReminderMutation.mutateAsync({
          id: invoiceId
        })
        return Boolean(res)
      }}
      data-testid="modal.send-payment-reminder"
    >
      Send a reminder email to {customerLegalCompanyName} for invoice{' '}
      {invoiceNumber}?
    </SimpleModal>
  )
}
